<template>
  <div 
    class="bar" 
    ref="bar" 
    @mousemove="toActiveHover" 
    @mouseleave="toDeactivateHover"
    @pointerdown="startDrag"
    @touchstart="startDrag"
  >
    <div class="bar__base-line"></div>
    <!--Отображение прогресса буферизации аудио-->
    <ul class="bar__bufered-list" v-if="isFocusTheAudio">
      <li 
        class="bar__bufered-item" 
        v-for="(item, index) in buferedList"
        :key="index"
        :style="{
          left: `${(item.start / podcast.audio_length) * 100}%`,
          width: `${((item.end - item.start) / podcast.audio_length) * 100}%`
        }">
      </li>
    </ul>
    <!--Отображение прогресса-->
    <div 
      class="bar__progress-line" 
      :class="{ 'bar__progress-line--touch-active': isTouchDragging }" 
      :style="{ width: `${progressPercent}%` }" 
      v-if="isFocusTheAudio">
    </div>
    <!-- Hover-полоса поверх всего -->
    <div class="bar__hover" :style="{ width: `${hoverPosition}px` }" v-if="isHovering">
      <div class="bar__hover-progress" :style="{ width: `${getCurrentTimePx}px` }" v-if="isFocusTheAudio"></div>
    </div>
    <!--Отображение диапазонов таймкодов-->
    <ul class="bar__timecodes-list" v-if="timecodesList">
      <li v-for="timecodeWidth in timecodesList" class="bar__timecodes-item" :style="`width: ${timecodeWidth}%`"></li>
    </ul>
  </div>
</template>

<script setup>
  import { computed, ref, onBeforeUnmount } from 'vue';
  import { useStore } from 'vuex';
  import { useAudioControl } from '../../composables/audioControl';
  import { useAudioPlayerState } from '../../composables/computedValues';
  import { throttle } from 'lodash';
  //-----------------------
  const store = useStore();
  const props = defineProps({
    //ID подкаста, к которому относится этот прогресс бар
    idPodcast: { type: Number, required: true },
  });
  //-----------------------
  const bar = ref(null);
  const hoverPosition = ref(0);
  const isHovering = ref(false);
  const isDragging = ref(false);
  const interactionType = ref(null);
  const isTouchDragging = ref(false);
  //-----------------------
  const { seekToTime } = useAudioControl();
  //-----------------------
  const { podcast, isPlaying, speed, isAnyLoading, isReady, currentTime } = useAudioPlayerState();
  //-----------------------
  onBeforeUnmount(() => {
    removeEventListeners();
  });
  //-----------------------
  //Контекст подкаста
  const ctxPodcast = computed(() => store.state.Audioplayer.list[props.idPodcast]);
  //Список диапазонов времени подгруженных в аудио
  const buferedList = computed(() => {
    return store.state.Audioplayer.bufferedRanges;
  });
  //Фокус на этом подкасте
  const isFocusTheAudio = computed(() => {
    return store.state.Audioplayer.activePodcastId == props.idPodcast;
  });
  //Получить массив продолжительности каждого таймкода
  const timecodesLength = computed(() => {
    const timecodes = ctxPodcast.value?.timecodes || {};
    const duration = ctxPodcast.value?.audio_length || 1;
    const entries = Object.entries(timecodes)
      .map(([time]) => parseFloat(time))
      .sort((a, b) => a - b);

    const result = [];
    for (let i = 0; i < entries.length; i++) {
      const start = entries[i];
      const end = entries[i + 1] ?? duration;
      result.push(end - start);
    }
    return result;
  });
  //Список таймкодов
  const timecodesList = computed(() => {
    const durations = timecodesLength.value;
    const total = ctxPodcast.value?.audio_length || 1;

    return durations.map(d => (d / total) * 100);
  });
  //Процент текущего времени прослушивания
  const progressPercent = computed(() => {
    const duration = podcast.value?.audio_length || 1;
    return (currentTime.value / duration) * 100;
  });

  const getCurrentTimePx = computed(() => {
    const duration = podcast.value?.audio_length || 1;
    const width = bar.value?.offsetWidth || 1;
    return (currentTime.value / duration) * width;
  });

  const updateProgress = (e) => {
    if (!bar.value || !podcast.value) return;
    const rect = bar.value.getBoundingClientRect();
    const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
    let position = (clientX - rect.left) / rect.width;
    position = Math.max(0, Math.min(1, position));
    const newTime = position * podcast.value.audio_length;
    seekToTime(newTime, props.idPodcast);
  };

  const toActiveHover = (e) => {
    if (!bar.value) return;
    isHovering.value = true;
    const rect = bar.value.getBoundingClientRect();
    const x = e.clientX - rect.left;
    hoverPosition.value = Math.max(0, Math.min(x, rect.width));
  };

  const toDeactivateHover = () => {
    isHovering.value = false;
  };

  const startDrag = (e) => {
    if (e.pointerType === 'touch') {
      isTouchDragging.value = true;
      e.preventDefault();
    }

    isDragging.value = true;
    throttledUpdateProgress(e);
    addEventListeners();
  };

  const handleDrag = (e) => {
    if (!isDragging.value) return;
    if (e.type === 'touchmove') e.preventDefault();
    throttledUpdateProgress(e);
  };

  const stopDrag = () => {
    isDragging.value = false;
    isTouchDragging.value = false;
    removeEventListeners();
  };

  const throttledUpdateProgress = throttle((e) => {
    if (!bar.value || !podcast.value) return;
    const rect = bar.value.getBoundingClientRect();
    const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
    let position = (clientX - rect.left) / rect.width;
    position = Math.max(0, Math.min(1, position));
    const newTime = position * podcast.value.audio_length;
    seekToTime(newTime, props.idPodcast);
  }, 300);

  const addEventListeners = () => {
    window.addEventListener('pointermove', handleDrag);
    window.addEventListener('pointerup', stopDrag);
    window.addEventListener('pointercancel', stopDrag);
    window.addEventListener('pointerleave', stopDrag);
  };

  const removeEventListeners = () => {
    window.removeEventListener('pointermove', handleDrag);
    window.removeEventListener('pointerup', stopDrag);
    window.removeEventListener('pointercancel', stopDrag);
    window.removeEventListener('pointerleave', stopDrag);
  };
</script>

<style lang="scss" scoped>
  @import './styles.scss';
</style>